import Box from "@mui/material/Box";
import NodeSkeleton from "./NodeSkeleton";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import React, { memo } from "react";
import { type NodeProps } from "@xyflow/react";
import type { ExplorerNode } from "../../declarations/IExplorerTypes";
import { getNodeColor, getNodeIcon } from "./nodeColors";
import NodeListCard from "./NodeListCard";
import NodeListRow, { NodeRowChevron } from "./NodeListRow";
import NodeToolbar from "./NodeToolbar";
import CountryFlag from "./CountryFlag";

function ReleasedProductListNode({ data }: NodeProps<ExplorerNode>) {
    const items = data.releasedProducts ?? [];
    const expandedRPs = data.expandedReleasedProducts ?? [];
    const isLoading = data.status === "loading";
    const nodeColor = getNodeColor(data.entityType);

    return (
        <NodeListCard
            nodeColor={nodeColor}
            isSelected={data.isSelected}
            isLoading={isLoading}
            width={240}
        >
            <NodeToolbar
                nodeColor={nodeColor}
                icon={getNodeIcon(data.entityType)}
                title={data.nodeTitle}
                subtitle={data.nodeSubtitle}
                global
            />

            {isLoading ? (
                <NodeSkeleton rows={3} variant="list" />
            ) : items.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                    No released products
                </Typography>
            ) : (
                <List dense disablePadding sx={{ mt: 0.5 }}>
                    {items.map((item, index) => {
                        const nodeId = `ReleasedProduct-${item.itemNumber}|${item.dataAreaId}`;
                        const rpKey = `${item.itemNumber}|${item.dataAreaId}`;
                        const isExpanded = expandedRPs.includes(rpKey);
                        return (
                            <React.Fragment key={nodeId}>
                                {index > 0 && <Divider sx={{ mx: 0.5, opacity: 0.4 }} />}
                                <NodeListRow
                                    nodeColor={nodeColor}
                                    isExpanded={isExpanded}
                                    onClick={() => data.onReleasedProductSelect?.(nodeId)}
                                >
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <CountryFlag dataAreaId={item.dataAreaId} size={14} />
                                        <Typography variant="caption" fontWeight={400} sx={{ lineHeight: 1.3 }}>
                                            {item.itemNumber}
                                        </Typography>
                                        <Box sx={{ flex: 1 }} />
                                        <NodeRowChevron isExpanded={isExpanded} nodeColor={nodeColor} />
                                    </Stack>
                                </NodeListRow>
                            </React.Fragment>
                        );
                    })}
                </List>
            )}
        </NodeListCard>
    );
}

export default memo(ReleasedProductListNode);
